<script setup>
import GuestLayout from '@/Layouts/GuestLayout.vue'
import InputError from '@/Components/InputError.vue'
import { useForm, usePage } from '@inertiajs/vue3'
import { onMounted, ref } from 'vue'

const form = useForm({
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
})

const rewardfulReferral = ref('')

onMounted(() => {
  window.addEventListener('Rewardful.initialized', () => {
    rewardfulReferral.value = window.Rewardful.referral || ''
  })
})

const errors = usePage().props.errors

const submit = () => {
  form.post(route('register', { affiliateId: rewardfulReferral.value }), {
    onFinish: () => form.reset('password', 'password_confirmation'),
  })
}
</script>

<template>
  <GuestLayout>
    <div class="mx-auto max-w-xl rounded-xl bg-white p-6 shadow-lg shadow-gray-800/5 ring-1 ring-gray-900/5">
      <h2 class="mb-5 text-center text-2xl font-semibold text-gray-900">Sign up with email</h2>

      <!-- form -->
      <div>
        <form class="space-y-6" @submit.prevent="submit">
          <div>
            <label for="name" class="block text-sm font-medium leading-6 text-gray-900">Name</label>
            <div class="mt-2">
              <input
                id="name"
                v-model="form.name"
                name="name"
                type="text"
                required
                class="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 duration-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
              />
              <InputError class="mt-2" :message="form.errors.name" />
            </div>
          </div>
          <div>
            <label for="email" class="block text-sm font-medium leading-6 text-gray-900">Email address</label>
            <div class="mt-2">
              <input
                id="email"
                v-model="form.email"
                name="email"
                type="email"
                required
                class="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 duration-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
              />
              <InputError class="mt-2" :message="form.errors.email" />
            </div>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium leading-6 text-gray-900">Password</label>
            <div class="mt-2">
              <input
                id="password"
                v-model="form.password"
                autocomplete
                name="password"
                type="password"
                required
                class="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 duration-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
              />
              <InputError class="mt-2" :message="form.errors.password" />
            </div>
          </div>

          <div>
            <label for="password_confirm" class="block text-sm font-medium leading-6 text-gray-900">
              Confirm Password
            </label>
            <div class="mt-2">
              <input
                id="password_confirm"
                v-model="form.password_confirmation"
                autocomplete
                name="password_confirm"
                type="password"
                required
                class="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 duration-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
              />
              <InputError class="mt-2" :message="form.errors.password_confirmation" />
            </div>
          </div>

          <div class="text-center">
            <button
              type="submit"
              :class="{ 'opacity-25': form.processing }"
              :disabled="form.processing"
              class="inline-flex w-full justify-center rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white duration-300 hover:bg-slate-700"
            >
              Continue
              <span v-if="form.processing" class="ml-2">
                <svg
                  class="h-5 w-5 animate-spin text-gray-200 sm:inline"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </span>
              <span v-else aria-hidden="true" class="ml-2 text-gray-200 sm:inline">→</span>
            </button>
          </div>
        </form>
      </div>

      <div class="mx-auto">
        <div class="relative mt-6">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-t border-gray-200"></div>
          </div>
          <div class="relative flex justify-center text-sm font-medium leading-6">
            <span class="bg-white px-6 text-gray-900">Or continue with</span>
          </div>
        </div>

        <div class="mt-6 space-y-6">
          <div>
            <a
              :href="route('etsy.auth')"
              class="relative inline-flex w-full items-center justify-between gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-center text-sm font-medium text-gray-800 shadow-sm ring-orange-200 ring-offset-2 transition-all duration-300 hover:border-orange-300 hover:bg-orange-100 hover:text-current active:ring-2"
            >
              <img src="/images/logo-clouds/etsy-logo.png" alt="Etsy logo" class="absolute w-12 object-contain" />
              <span class="w-full text-sm font-semibold leading-6">Continue with Etsy</span>
            </a>
            <InputError class="mt-2" :message="errors.message" />
          </div>

          <!--          <a href="#" class="inline-flex w-full items-center justify-between gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-800 shadow-sm ring-orange-200 ring-offset-2 transition-all duration-300  hover:border-orange-300 hover:bg-orange-100 hover:text-current active:ring-2">-->
          <!--            <svg class="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">-->
          <!--              <path d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z" fill="#EA4335"></path>-->
          <!--              <path d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z" fill="#4285F4"></path>-->
          <!--              <path d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z" fill="#FBBC05"></path>-->
          <!--              <path d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z" fill="#34A853"></path>-->
          <!--            </svg>-->
          <!--            <span class="text-sm font-semibold leading-6">Continue with Google</span>-->
          <!--            <div></div>-->
          <!--          </a>-->
        </div>
      </div>

      <p class="mt-5 text-center text-sm text-gray-500">
        Already have an account?
        <a class="text-orange-500 hover:underline" :href="route('login')">Sign in</a>
      </p>

      <!-- Conditions -->
      <p class="mt-2 text-center text-sm text-gray-500">
        By signing up you agree to Prelist’s
        <a target="_blank" class="text-orange-500 hover:underline" href="https://prelist.io/documents/terms-of-service">
          terms and conditions
        </a>
      </p>
    </div>
  </GuestLayout>
</template>
